import { useNavigate } from "react-router-dom";
import React, { useMemo } from "react";
import { findDataInFields } from "../../../utils/helperFuctions";

const STATUS_STYLES = {
  "Picked up": "bg-amber-100 text-amber-800 ring-amber-600/20",
  Returned: "bg-emerald-100 text-emerald-800 ring-emerald-600/20",
  default: "bg-gray-100 text-gray-700 ring-gray-500/10",
};

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.default;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}
      title={status}
    >
      <span className="block h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {status}
    </span>
  );
}

function VehicleCard({ v, onViewVehicle, onViewReservation }) {
  return (
    <div className="group rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition hover:shadow-md sm:p-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-gray-900 font-semibold leading-6">
            {v.brand} {v.model}
          </div>
          <div className="text-gray-500 text-sm">{v.name}</div>
        </div>
        <StatusBadge status={v.reservation_status} />
      </div>

      {/* Body */}
      <div className="mt-4 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
        <div className="rounded-xl bg-gray-50 px-3 py-2">
          <div className="text-gray-500 text-xs">Plate</div>
          <div className="font-medium text-gray-900">{v.plate}</div>
        </div>
        <div className="rounded-xl bg-gray-50 px-3 py-2">
          <div className="text-gray-500 text-xs">Year</div>
          <div className="font-medium text-gray-900">{v.year}</div>
        </div>
        <div className="rounded-xl bg-gray-50 px-3 py-2 col-span-2 sm:col-span-1">
          <div className="text-gray-500 text-xs">Class</div>
          <div className="font-medium text-gray-900 truncate" title={v.class}>
            {v.class}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <svg
            className="h-4 w-4 opacity-70"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path d="M16.707 5.293a1 1 0 0 1 0 1.414l-7.25 7.25a1 1 0 0 1-1.414 0l-3-3a1 1 0 1 1 1.414-1.414l2.293 2.293 6.543-6.543a1 1 0 0 1 1.414 0z" />
          </svg>
          ID: {v.id}
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700 transition hover:bg-gray-50 active:scale-[.99] focus:outline-none focus:ring-2 focus:ring-blue-200"
            onClick={() => onViewVehicle && onViewVehicle(v)}
          >
            Vehicle
          </button>
          <button
            type="button"
            className="rounded-xl border border-gray-200 px-3 py-1.5 text-xs font-medium text-blue-700 transition hover:bg-blue-50 active:scale-[.99] focus:outline-none focus:ring-2 focus:ring-blue-200"
            onClick={() => onViewReservation && onViewReservation(v)}
          >
            Reservation
          </button>
        </div>
      </div>
    </div>
  );
}

const CompanyVehicles = ({ currentFields }) => {
  const navigate = useNavigate();
  const vehicles = useMemo(
    () => findDataInFields(currentFields, "vehicles.defaultValue") || [],
    [currentFields]
  );

  const statusCounts = useMemo(() => {
    if (!vehicles || vehicles.length === 0) {
      return {};
    }
    return vehicles.reduce((acc, vehicle) => {
      const status = vehicle.reservation_status || "Unknown";
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
  }, [vehicles]);

  if (!vehicles || vehicles.length === 0) {
    return (
      <div className="w-full rounded-2xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
        No vehicles found.
      </div>
    );
  }

  const statusEntries = Object.entries(statusCounts);

  return (
    <div className="w-full">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-gray-900">
          Company vehicles
        </h3>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          {statusEntries.map(([status, count], index) => (
            <React.Fragment key={status}>
              <span>
                {status}: {count}
              </span>
              {index < statusEntries.length - 1 && (
                <span className="text-gray-300">|</span>
              )}
            </React.Fragment>
          ))}
          <span className="font-semibold text-gray-700">
            Total: {vehicles.length}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {vehicles.map((v) => (
          <VehicleCard
            key={v.id}
            v={v}
            onViewVehicle={() => navigate(`/vehicles/${v.id}`)}
            onViewReservation={() =>
              navigate(`/reservations/${v.reservation_id}`)
            }
          />
        ))}
      </div>
    </div>
  );
};

export default CompanyVehicles;
